/** A system's record page: header with gauge, facts, gaps, relations, contained systems, shelf. */
import { Link, useParams, useSearchParams } from 'react-router'
import { CollectionFilter } from '../components/Filters'
import { Plus } from 'lucide-react'
import { BookShelf, SeriesShelf, ShelfSkeleton, SystemShelf } from '../components/Cards'
import { Chips, Empty, ErrorState, Loading, Pager, usePage } from '../components/Bits'
import {
  Attributions,
  CategoryRail,
  ExternalIds,
  Facts,
  GapList,
  LinkList,
  MergedRedirect,
  Prose,
  RecordHeader,
  RelationList,
  type Crumb,
} from '../components/Record'
import { qs } from '../lib/api'
import { categoryLabel, plural } from '../lib/format'
import {
  CHARACTER_STYLE_LABELS,
  COMPLEXITY_LABELS,
  GAME_SIZE_LABELS,
  label,
  PLAY_MODE_LABELS,
  STATUS_LABELS,
  SYSTEM_RELATION_INVERSE,
  SYSTEM_RELATION_LABELS,
} from '../lib/labels'
import { BuilderLinks } from '../components/BuilderLinks'
import { isOnePageRpg } from '../../shared/model'
import { useQuery } from '../lib/query'
import { signInHref, useSession } from '../lib/session'
import { useTitle } from '../lib/useTitle'
import type { BookCard, Page, SystemDetail } from '../lib/types'

const CONTAINER_TEXT: Record<string, string> = {
  parent: 'Editions of one game',
  agnostic: 'System-agnostic collection',
  'one-page': 'One-page RPG collection',
  family: 'Family of related systems',
  publisher: "One publisher's systems",
  generic: 'Collection',
}

function players(min: number | null, max: number | null): string {
  if (min && max) return min === max ? `${min}` : `${min}–${max}`
  if (min) return `${min} or more`
  if (max) return `Up to ${max}`
  return ''
}

export function SystemPage() {
  const { id = '' } = useParams()
  const session = useSession()
  const [params, setParams] = useSearchParams()
  const category = params.get('category') ?? ''
  const page = usePage()
  const { data: s, error, refresh } = useQuery<SystemDetail>(`/api/v1/systems/${id}`)
  const { data: books } = useQuery<Page<BookCard>>(
    `/api/v1/systems/${id}/books${qs({ category, page, owned: params.get('owned'), limit: 48 })}`,
  )
  useTitle(s?.doc.name)

  if (error) return <ErrorState error={error} retry={refresh} />
  if (!s) return <Loading label="Loading system" />
  if (s.merged_into) return <MergedRedirect type="system" to={s.merged_into} />
  const d = s.doc
  const crumbs: Crumb[] = [{ label: 'Systems', to: '/systems' }]
  if (s.parent) crumbs.push({ label: s.parent.name, to: `/systems/${s.parent.id}` })
  const addBook = `/books/new?system=${s.id}`
  const wargame = d.game_type === 'wargame'

  const selectCategory = (c: string) => {
    const next = new URLSearchParams(params)
    if (c) next.set('category', c)
    else next.delete('category')
    next.delete('page')
    setParams(next, { replace: true })
  }

  return (
    <article className="page record">
      <RecordHeader
        type="system"
        id={s.id}
        crumbs={crumbs}
        title={d.name}
        subtitle={
          <>
            {[wargame ? 'Wargame' : '', d.edition, d.year, d.system_family].filter(Boolean).join(' · ')}
            {d.status && d.status !== 'active' && <span className="tag-quiet">{label(STATUS_LABELS, d.status)}</span>}
            {d.container_kind && <span className="tag-quiet">{CONTAINER_TEXT[d.container_kind]}</span>}
          </>
        }
        cover={s.cover}
        onePage={isOnePageRpg(d)}
        score={s.completeness.score}
        editHref={`/systems/${s.id}/edit`}
        historyHref={`/systems/${s.id}/history`}
        pending={s.pending_edits}
        pendingHref={`/review?entity=${s.id}`}
        deleted={s.deleted}
      >
        {d.publishers.length > 0 && (
          <p className="record-byline">
            {d.publishers.map((p, i) => (
              <span key={p.id ?? p.name}>
                {i > 0 && ', '}
                {p.id ? <Link to={`/publishers/${p.id}`}>{p.name}</Link> : p.name}
              </span>
            ))}
          </p>
        )}
        {d.aliases.length > 0 && <p className="record-aliases">Also known as {d.aliases.join(', ')}</p>}
      </RecordHeader>

      <div className="record-body">
        <div className="record-main">
          <Prose text={d.description} />
          <Attributions attributions={d.attributions} />
          <GapList missing={s.completeness.missing} score={s.completeness.score} editHref={`/systems/${s.id}/edit`} />
          <RelationList
            type="system"
            outgoing={d.relations.map((r) => ({ type: r.type, id: r.system.id, name: r.system.name }))}
            incoming={s.incoming}
            labels={SYSTEM_RELATION_LABELS}
            inverse={SYSTEM_RELATION_INVERSE}
          />
        </div>
        <aside className="record-side">
          <Facts
            facts={[
              { label: 'Parent system', value: d.parent_system },
              { label: 'Edition', value: d.edition },
              {
                label: 'Family',
                value: d.system_family && (
                  <Link to={`/systems?family=${encodeURIComponent(d.system_family)}`}>{d.system_family}</Link>
                ),
              },
              { label: 'Released', value: d.year },
              { label: 'Status', value: d.status ? label(STATUS_LABELS, d.status) : '' },
              { label: 'Rules weight', value: d.complexity ? label(COMPLEXITY_LABELS, d.complexity) : '' },
              ...(wargame
                ? [
                    {
                      label: 'Game sizes',
                      value: d.game_sizes.length > 0 && (
                        <Chips
                          items={d.game_sizes.map((g) => label(GAME_SIZE_LABELS, g))}
                          hrefFor={(g) => {
                            const key = d.game_sizes.find((x) => label(GAME_SIZE_LABELS, x) === g)
                            return `/systems?game_size=${key ?? ''}`
                          }}
                        />
                      ),
                    },
                    { label: 'Scales', value: d.miniature_scales.join(', ') },
                    { label: 'Players', value: players(d.players_min, d.players_max) },
                    { label: 'Ways to play', value: d.session_styles.join(', ') },
                  ]
                : [
                    {
                      label: 'Ways to play',
                      value: d.play_modes.length > 0 && (
                        <Chips items={d.play_modes.map((m) => label(PLAY_MODE_LABELS, m))} />
                      ),
                    },
                    { label: 'Players', value: players(d.players_min, d.players_max) },
                    { label: 'Sessions', value: d.session_styles.join(', ') },
                    {
                      label: 'Characters',
                      value: d.character_styles.map((m) => label(CHARACTER_STYLE_LABELS, m)).join(', '),
                    },
                  ]),
              { label: 'Mechanics', value: d.mechanics.length > 0 && <Chips items={d.mechanics} /> },
              { label: 'License', value: d.license },
              {
                label: 'Genres',
                value: d.genres.length > 0 && (
                  <Chips items={d.genres} hrefFor={(g) => `/systems?genre=${encodeURIComponent(g)}`} />
                ),
              },
              { label: 'Dice & materials', value: d.dice_materials.length > 0 && <Chips items={d.dice_materials} /> },
              {
                label: 'Tags',
                value: d.tags.length > 0 && (
                  <Chips items={d.tags} hrefFor={(t) => `/systems?tag=${encodeURIComponent(t)}`} />
                ),
              },
              { label: 'Elsewhere', value: d.external_ids.length > 0 && <ExternalIds ids={d.external_ids} /> },
              {
                label: 'Flags',
                value: [
                  d.is_system_agnostic && 'System-agnostic',
                  d.is_one_page && 'One-page RPG',
                  d.is_explicit && 'Explicit content',
                ]
                  .filter(Boolean)
                  .join(', '),
              },
            ]}
          />
          <LinkList title="Links" links={d.urls} />
        </aside>
      </div>

      {s.children.length > 0 && (
        <section className="section">
          <h2 className="section-title">{d.container_kind === 'parent' ? 'Editions' : 'Systems in this collection'}</h2>
          <SystemShelf items={s.children} label="Contained systems" />
        </section>
      )}

      <BuilderLinks title={wargame ? 'Army builders' : 'Character builders'} links={d.character_builder_urls} />

      {wargame && (
        <section className="section">
          <div className="section-head">
            <h2 className="section-title">
              Factions{' '}
              {d.factions.length > 0 && <span className="section-count">{plural(d.factions.length, 'faction')}</span>}
            </h2>
          </div>
          {d.factions.length === 0 ? (
            <p className="section-note">
              No factions listed yet.{' '}
              <Link to={session.user ? `/systems/${s.id}/edit` : signInHref(`/systems/${s.id}/edit`)}>Add them</Link> by
              name.
            </p>
          ) : (
            <Chips items={d.factions} hrefFor={(f) => `/search?q=${encodeURIComponent(f)}`} />
          )}
        </section>
      )}

      {s.series.length > 0 && (
        <section className="section">
          <h2 className="section-title">Settings & series</h2>
          <SeriesShelf items={s.series} />
        </section>
      )}

      <section className="section" aria-labelledby="shelf-title">
        <div className="section-head">
          <h2 className="section-title" id="shelf-title">
            Books <span className="section-count">{plural(s.book_count, 'book')}</span>
          </h2>
          <span className="section-links">
            <CollectionFilter />
            <Link to={session.user ? addBook : signInHref(addBook)} className="section-more">
              <Plus size={16} aria-hidden="true" /> Add a book
            </Link>
          </span>
        </div>
        <CategoryRail categories={s.categories} active={category} onSelect={selectCategory} label={categoryLabel} />
        {!books ? (
          <ShelfSkeleton count={8} />
        ) : books.items.length === 0 ? (
          <Empty title={s.book_count ? 'No books in this category' : 'No books yet'}>
            {s.children.length
              ? 'Books are filed under the systems above.'
              : 'Add the first one, or tag an existing book with this system.'}
          </Empty>
        ) : (
          <>
            <BookShelf items={books.items} label="Books" showSystems={books.items.some((b) => b.systems.length > 1)} />
            <Pager page={books.page} limit={books.limit} total={books.total} />
          </>
        )}
      </section>
    </article>
  )
}
